import { IndicatorDetails } from './utils';
import { ApolloClient, gql } from 'apollo-boost';

const query = gql`
  query reportAnswers($id: ID) {
    allReports(filters: { id: $id }) {
      edges {
        node {
          dbId
          selectedServiceId
          serviceName
          indicators {
            edges {
              node {
                dbId
                name
                stageId
                definition
                isHuman
              }
            }
          }
          npoIndicators {
            edges {
              node {
                indicatorId
                forecast
                finalForecast
                interventionDosageLevel
                interventionDosageType
                interventionDosageNumber
                happensAfterProgramYear
                timePeriod
                measurement
                file
                level1Forecast
                level1Guess
              }
            }
          }
        }
      }
    }
  }
`;

const mutation = gql`
  mutation newNpoIndicator(
    $id: Int
    $reportId: Int!
    $indicatorId: Int!
    $forecast: String
    $finalForecast: String
    $interventionDosageLevel: String
    $interventionDosageType: String
    $interventionDosageNumber: String
    $happensAfterProgramYear: String
    $timePeriod: String
    $measurement: String
    $file: String
    $level1Forecast: String
    $level1Guess: String
    $level1IsHuman: String
    $level1ForecastResultOne: String
  ) {
    newNpoIndicator(
      id: $id
      reportId: $reportId
      indicatorId: $indicatorId
      forecast: $forecast
      finalForecast: $finalForecast
      interventionDosageLevel: $interventionDosageLevel
      interventionDosageType: $interventionDosageType
      interventionDosageNumber: $interventionDosageNumber
      happensAfterProgramYear: $happensAfterProgramYear
      timePeriod: $timePeriod
      measurement: $measurement
      file: $file
      level1Forecast: $level1Forecast
      level1Guess: $level1Guess
      level1IsHuman: $level1IsHuman
      level1ForecastResultOne: $level1ForecastResultOne
    ) {
      success
      id
    }
  }
`;

const bulkUpdateMutation = gql`
  mutation bulkUpdateNpoIndicators($indicators: [IndicatorInput!]!) {
    bulkUpdateNpoIndicators(indicators: $indicators) {
      success
      updatedCount
      failedCount
      errors
    }
  }
`;

export const getReportIndicators = (
  client: ApolloClient<object>,
  reportId: number | string | undefined,
) => {
  return client.query({
    query,
    variables: { id: reportId },
    fetchPolicy: 'network-only',
  });
};

export interface SaveReportIndicatorsVariables {
  reportId: number | string | undefined;
  file?: IndicatorDetails['file'];
  forecast?: IndicatorDetails['forecast'] | null;
  finalForecast?: IndicatorDetails['finalForecast'] | null;
  timePeriod?: IndicatorDetails['timePeriod'];
  indicatorId: IndicatorDetails['indicatorId'];
  measurement?: IndicatorDetails['measurement'];
  happensAfterProgramYear?: IndicatorDetails['happensAfterProgramYear'];
  interventionDosageLevel?: IndicatorDetails['interventionDosageLevel'];
  interventionDosageType?: IndicatorDetails['interventionDosageType'];
  interventionDosageNumber?: IndicatorDetails['interventionDosageNumber'];
  level1Forecast?: IndicatorDetails['level1Forecast'];
  level1Guess?: IndicatorDetails['level1Guess'];
  level1IsHuman?: IndicatorDetails['level1IsHuman'];
  level1ForecastResultOne?: IndicatorDetails['level1ForecastResultOne'];
}

// fixes TIPD-2506
const processFileField = (
  file: IndicatorDetails['file'] | undefined,
): string | undefined => {
  if (!file) return undefined;
  return typeof file === 'string' ? file : JSON.stringify(file);
};

export const saveReportIndicators = (
  client: ApolloClient<object>,
  variables: SaveReportIndicatorsVariables,
) => {
  return client.mutate({
    mutation,
    variables: {
      ...variables,
      file: processFileField(variables.file),
    },
    fetchPolicy: 'no-cache',
  });
};

export interface BulkUpdateIndicatorInput
  extends SaveReportIndicatorsVariables {
  id?: number;
}

export const bulkUpdateReportIndicators = (
  client: ApolloClient<object>,
  indicators: BulkUpdateIndicatorInput[],
) => {
  const processedIndicators = indicators.map(indicator => ({
    ...indicator,
    file: processFileField(indicator.file),
  }));

  return client.mutate({
    mutation: bulkUpdateMutation,
    variables: {
      indicators: processedIndicators,
    },
    fetchPolicy: 'no-cache',
  });
};
